// Reine (React-freie) Logik für den EntryEditWizard — getrennt von der
// Komponente, damit Anker-Suche, Vorauswahl und Labels unabhängig testbar sind.

import type { Bo4eTarget, EdiAnchor } from "@/api/mapperApi";

/** Eindeutiger Schlüssel eines Skelett-Ankers (nr allein reicht nicht —
 * ein Segment hat mehrere Datenelemente/Komponenten unter derselben nr). */
export function anchorKey(a: EdiAnchor): string {
  return [a.nr ?? "", a.data_element ?? "", a.component ?? "", a.qualifier ?? ""].join("|");
}

/** Kompaktes BA-Label für den Kontext-Chip im Wizard-Kopf:
 * `NAD+MS · MP-ID Absender · DE3039 MP-ID` (fehlende Teile werden weggelassen). */
export function anchorChipLabel(a: EdiAnchor): string {
  const seg = `${a.segment}${a.qualifier ? `+${a.qualifier}` : ""}`;
  const parts = [seg];
  if (a.seg_name) parts.push(a.seg_name);
  if (a.data_element) parts.push(`DE${a.data_element}${a.de_name ? ` ${a.de_name}` : ""}`);
  return parts.join(" · ");
}

/** Technische Detail-Zeile eines Ankers (Zeile 2 in der Auswahl-Liste):
 * `SG2 / NAD+MS · nr 00008 · DE3039 MP-ID`. */
export function anchorDetailLine(a: EdiAnchor): string {
  const seg = `${a.segment_group ? `${a.segment_group} / ` : ""}${a.segment}${a.qualifier ? `+${a.qualifier}` : ""}`;
  const parts = [seg];
  if (a.nr) parts.push(`nr ${a.nr}`);
  if (a.data_element) parts.push(`DE${a.data_element}${a.de_name ? ` ${a.de_name}` : ""}`);
  return parts.join(" · ");
}

/** Filtert Skelett-Anker für Schritt 1.
 * - `segment` (z.B. "NAD"): nur Anker dieses Segment-Tags; `null` = alle.
 * - `query`: whitespace-getrennte Begriffe, ALLE müssen matchen (UND),
 *   case-insensitiver Substring über segment, qualifier, nr, data_element,
 *   seg_name, de_name, example und value_pool. */
export function filterAnchors(
  anchors: EdiAnchor[],
  query: string,
  segment: string | null,
): EdiAnchor[] {
  let out = segment ? anchors.filter((a) => a.segment === segment) : anchors;
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return out;
  return out.filter((a) => {
    const hay = [
      a.segment,
      a.qualifier ?? "",
      a.nr ?? "",
      a.data_element ?? "",
      a.seg_name ?? "",
      a.de_name ?? "",
      a.example ?? "",
      (a.value_pool ?? []).join(" "),
    ]
      .join(" ")
      .toLowerCase();
    return terms.every((t) => hay.includes(t));
  });
}

/** Filtert BO4E-Ziele für Schritt 2 (gleiches UND-Prinzip wie filterAnchors;
 * matcht über `bo.path`, type und enum-Werte). */
export function filterTargets(targets: Bo4eTarget[], query: string): Bo4eTarget[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (terms.length === 0) return targets;
  return targets.filter((t) => {
    const hay = [`${t.bo}.${t.path}`, t.type ?? "", (t.enum ?? []).join(" ")]
      .join(" ")
      .toLowerCase();
    return terms.every((q) => hay.includes(q));
  });
}

/** Vorauswahl Schritt 1: aktueller Entry-Anker im Skelett wiederfinden.
 * Match über nr + data_element (nur wenn nr gesetzt ist); bei mehreren
 * Kandidaten entscheidet zusätzlich component, sonst der erste Treffer. */
export function findInitialAnchor(
  anchors: EdiAnchor[],
  current: EdiAnchor,
): EdiAnchor | null {
  if (!current.nr) return null;
  const candidates = anchors.filter(
    (a) => a.nr === current.nr && (a.data_element ?? null) === (current.data_element ?? null),
  );
  if (candidates.length === 0) return null;
  const exact = candidates.find((a) => (a.component ?? null) === (current.component ?? null));
  return exact ?? candidates[0];
}

/** Vorauswahl Schritt 2: aktuelles bo4e_anchor im Skelett wiederfinden
 * (case-insensitiv über bo + path). `null` = Excel-Altlast, nicht im Skelett. */
export function findInitialTarget(
  targets: Bo4eTarget[],
  current: { bo: string; path: string },
): Bo4eTarget | null {
  const want = `${current.bo}.${current.path}`.toLowerCase();
  return targets.find((t) => `${t.bo}.${t.path}`.toLowerCase() === want) ?? null;
}

/** DE2379-Datumsformate (EDIFACT) mit BA-Labels für den date_format-Select. */
export const DATE_FORMATS: Array<{ code: string; label: string }> = [
  { code: "102", label: "102 — CCYYMMDD (Datum)" },
  { code: "203", label: "203 — CCYYMMDDHHMM (Datum + Uhrzeit)" },
  { code: "204", label: "204 — CCYYMMDDHHMMSS (Datum + Uhrzeit + Sekunden)" },
  { code: "303", label: "303 — CCYYMMDDHHMM+Zone (mit Zeitzonen-Offset)" },
  { code: "602", label: "602 — CCYY (nur Jahr)" },
  { code: "610", label: "610 — CCYYMM (Jahr + Monat)" },
];

/** Vorauswahl des DE2379-Formats: bestehender transform.edi_qualifier gewinnt;
 * sonst — wenn der Anker DE2379 ist und sein value_pool genau einen bekannten
 * Code enthält — dieser Code; sonst "102". */
export function initialDateFormat(
  anchor: EdiAnchor | null,
  existingQualifier: string | null | undefined,
): string {
  const known = new Set(DATE_FORMATS.map((f) => f.code));
  if (existingQualifier && known.has(existingQualifier)) return existingQualifier;
  if (
    anchor?.data_element === "2379" &&
    anchor.value_pool?.length === 1 &&
    known.has(anchor.value_pool[0])
  ) {
    return anchor.value_pool[0];
  }
  return "102";
}

export interface LookupRow {
  edi: string;
  bo4e: string;
}

/** code_lookup-Zeilen aus einem bestehenden transform.table — sonst eine Leerzeile. */
export function lookupRowsFromTable(table: Record<string, string> | null | undefined): LookupRow[] {
  const entries = Object.entries(table ?? {});
  if (entries.length === 0) return [{ edi: "", bo4e: "" }];
  return entries.map(([edi, bo4e]) => ({ edi, bo4e }));
}

/** Nur vollständige Zeilen (beide Seiten gefüllt) → transform.table. */
export function lookupTableFromRows(rows: LookupRow[]): Record<string, string> {
  const table: Record<string, string> = {};
  for (const r of rows) {
    const edi = r.edi.trim();
    const bo4e = r.bo4e.trim();
    if (edi && bo4e) table[edi] = bo4e;
  }
  return table;
}

export type CoreTransformType = "identity" | "code_lookup" | "date_format" | "const";

export const CORE_TRANSFORMS: Array<{ type: CoreTransformType; label: string; hint: string }> = [
  { type: "identity", label: "identity", hint: "Wert wird unverändert übernommen" },
  { type: "code_lookup", label: "code_lookup", hint: "EDI-Code → BO4E-Wert übersetzen" },
  { type: "date_format", label: "date_format", hint: "EDIFACT-Datum → ISO 8601" },
  { type: "const", label: "const", hint: "fester Wert" },
];

export function isCoreTransformType(t: string): t is CoreTransformType {
  return CORE_TRANSFORMS.some((c) => c.type === t);
}

/** Klartext-Beschreibung des Transforms für die Zusammenfassung (Schritt 4). */
export function describeTransform(
  mode: CoreTransformType | "advanced",
  detail: { table?: Record<string, string>; dateFormat?: string; constValue?: string; advancedType?: string },
): string {
  switch (mode) {
    case "identity":
      return "unverändert übernommen (identity)";
    case "code_lookup": {
      const pairs = Object.entries(detail.table ?? {});
      const sample = pairs
        .slice(0, 3)
        .map(([k, v]) => `${k}→${v}`)
        .join(", ");
      return `per Code-Tabelle übersetzt (${pairs.length} ${pairs.length === 1 ? "Paar" : "Paare"}${sample ? `: ${sample}${pairs.length > 3 ? ", …" : ""}` : ""})`;
    }
    case "date_format":
      return `als EDIFACT-Datum (Format ${detail.dateFormat ?? "?"}) nach ISO 8601 konvertiert`;
    case "const":
      return `durch den festen Wert „${detail.constValue ?? ""}" ersetzt (const)`;
    case "advanced":
      return `per erweiterter Regel verarbeitet (${detail.advancedType ?? "JSON"})`;
  }
}
